const DBG = () => !!window.ANI_COUNT_DEBUG;
const dlog = (...a) => { if (DBG()) console.log("[ani-count]", ...a); };

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function parseMs(v) {
  if (v == null) return 0;
  if (typeof v === "number") return Math.round(v);
  const s = String(v).trim().toLowerCase();
  if (!s) return 0;
  if (s.endsWith("ms")) {
    const n = Number.parseFloat(s.slice(0, -2));
    return Number.isFinite(n) ? Math.round(n) : 0;
  }
  if (s.endsWith("s")) {
    const n = Number.parseFloat(s.slice(0, -1));
    return Number.isFinite(n) ? Math.round(n * 1000) : 0;
  }
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function getDelayFromClassName(className) {
  const parts = String(className || "").split(" ");
  for (const part of parts) {
    if (!part.startsWith("ani-delay-")) continue;
    const n = Number.parseInt(part.slice("ani-delay-".length), 10);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function getDelayFor(el) {
  if (el?.dataset?.countingDelay) return parseMs(el.dataset.countingDelay);
  const scope = el?.closest?.(".ani-counting-scope");
  if (scope?.dataset?.aniCountDelay) return parseMs(scope.dataset.aniCountDelay);
  return getDelayFromClassName(scope?.className || "");
}

function isVisibleDeep(el) {
  if (!el || !el.isConnected) return false;
  let n = el;
  while (n && n.nodeType === 1) {
    const cs = getComputedStyle(n);
    if (cs.display === "none" || cs.visibility === "hidden" || Number.parseFloat(cs.opacity) <= 0.01) return false;
    n = n.parentElement;
  }
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const interH = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
  const interW = Math.max(0, Math.min(r.right, vw) - Math.max(r.left, 0));
  return interH > 0 && interW > 0;
}

function isInviewAncestorEntered(el) {
  const anc = el?.closest?.(".ani-when-inview, .ani-inview-scope, [data-ani-inview]");
  return !anc || anc.classList.contains("is-inview");
}

function isScopeReadyToStart(scope) {
  return isInviewAncestorEntered(scope) && isVisibleDeep(scope);
}

function dumpEl(el) {
  if (!el) return null;
  if (!(el instanceof Element)) {
    if (el === document) {
      return {
        tag: "#document",
        id: null,
        classes: "",
        opacity: null,
        display: null,
        visibility: null,
        w: null,
        h: null,
        top: null,
        bottom: null,
        inViewport: null,
        reduced: window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true
      };
    }
    return {
      tag: String(el?.constructor?.name || typeof el),
      id: null,
      classes: "",
      opacity: null,
      display: null,
      visibility: null,
      w: null,
      h: null,
      top: null,
      bottom: null,
      inViewport: null,
      reduced: window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true
    };
  }
  const cs = getComputedStyle(el);
  const r = el.getBoundingClientRect();
  const classes = typeof el.className === "string" ? el.className : (el.className?.baseVal || "");
  return {
    tag: el.tagName.toLowerCase(),
    id: el.id || null,
    classes,
    opacity: cs.opacity,
    display: cs.display,
    visibility: cs.visibility,
    w: Math.round(r.width),
    h: Math.round(r.height),
    top: Math.round(r.top),
    bottom: Math.round(r.bottom),
    inViewport: !(r.bottom <= 0 || r.top >= (window.innerHeight || 0)),
    reduced: window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true
  };
}

function animateCount(el, to, { duration = 1200, suffix = "", prefix = "", formatter, respectReduced = false, onComplete } = {}) {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const fmt = formatter || ((v) => Math.round(v).toString());
  const startTS = performance.now();
  const safeDur = Math.max(0, Number(duration) || 0);

  dlog("animate start", { to, duration, reduce, respectReduced, el: dumpEl(el), t: Math.round(startTS) });

  if ((reduce && respectReduced) || safeDur === 0) {
    el.textContent = `${prefix}${fmt(to)}${suffix}`;
    if (typeof onComplete === "function") onComplete();
    return;
  }

  let firstFrameLogged = false;

  function frame(now) {
    if (!firstFrameLogged) {
      dlog("animate first frame dt", Math.round(now - startTS));
      firstFrameLogged = true;
    }
    const p = Math.min(1, (now - startTS) / safeDur);
    const eased = easeOutCubic(p);
    const val = to * eased;
    el.textContent = `${prefix}${fmt(val)}${suffix}`;
    if (p < 1) {
      requestAnimationFrame(frame);
      return;
    }
    const endTS = performance.now();
    dlog("animate end", { elapsed: Math.round(endTS - startTS) });
    if (typeof onComplete === "function") onComplete();
  }

  requestAnimationFrame(frame);
}

function resetRowCountVisual(row) {
  const numEl = row?.querySelector?.(".counting-statement-number");
  if (!numEl) return;
  const suffix = row.dataset.countingSuffix ?? row.dataset.suffix ?? "";
  const prefix = row.dataset.countingPrefix ?? row.dataset.prefix ?? "";
  numEl.textContent = `${prefix}0${suffix}`;
}

function startCountingStatementsInScope(scope) {
  const rows = [...scope.querySelectorAll(".counting-statement-container")];
  if (!rows.length) {
    dlog("scope has no rows", dumpEl(scope));
    return;
  }

  const scopeStart = performance.now();
  let scheduled = 0;
  let finished = 0;

  dlog("scope COUNT START", { scope: dumpEl(scope), rows: rows.length, t: Math.round(scopeStart) });

  for (const row of rows) {
    if (row.dataset.countingDone === "1") continue;

    const numEl = row.querySelector(".counting-statement-number");
    const textEl = row.querySelector(".counting-statement-text");
    const rowDelay = getDelayFor(row);

    const runRow = () => {
      if (row.dataset.countingDone === "1") return;

      if (numEl) {
        const get = (k1, k2) => row.dataset[k1] ?? row.dataset[k2];
        const target = Number.parseFloat(get("countingTarget", "target") ?? "0");
        const suffix = get("countingSuffix", "suffix") ?? "";
        const duration = Number.parseInt(get("countingDuration", "duration") ?? "1200", 10);
        const prefix = get("countingPrefix", "prefix") ?? "";
        const respectReduced =
          row.hasAttribute("data-counting-respect-reduced") ||
          scope.hasAttribute("data-counting-respect-reduced");
        const formatter = (v) => Math.round(v).toString();

        resetRowCountVisual(row);
        dlog("row START", {
          row: dumpEl(row),
          numEl: dumpEl(numEl),
          target,
          duration,
          suffix,
          prefix,
          respectReduced,
          rowDelay
        });

        scheduled++;
        animateCount(numEl, target, {
          duration,
          suffix,
          prefix,
          formatter,
          respectReduced,
          onComplete: () => {
            finished++;
            dlog("row END", { row: dumpEl(row), finished, scheduled });
            if (finished >= scheduled) {
              const endTS = performance.now();
              dlog("scope COUNT END", {
                scope: dumpEl(scope),
                elapsed: Math.round(endTS - scopeStart),
                rowsTotal: rows.length
              });
            }
          }
        });
      }

      if (textEl) row.classList.add("is-active");
      row.dataset.countingDone = "1";
    };

    setTimeout(runRow, rowDelay);
  }
}

function waitForStableVisible(scope, handlers = {}) {
  const STABLE_MS = 120;
  const onPass = typeof handlers.onPass === "function" ? handlers.onPass : null;
  const onAbort = typeof handlers.onAbort === "function" ? handlers.onAbort : null;
  if (!isScopeReadyToStart(scope)) {
    if (onAbort) onAbort("not-ready-initial");
    return null;
  }
  let startedAt = performance.now();
  let rafId = null;

  function tick(now) {
    const visible = isScopeReadyToStart(scope);
    if (!visible) {
      rafId = null;
      if (onAbort) onAbort("lost-visibility");
      return;
    }
    if (now - startedAt >= STABLE_MS) {
      dlog("gate PASSED (stable)", { scope: dumpEl(scope), stableFor: Math.round(now - startedAt) });
      if (onPass) onPass();
      rafId = null;
      return;
    }
    rafId = requestAnimationFrame(tick);
  }

  rafId = requestAnimationFrame(tick);
  return () => {
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
      if (onAbort) onAbort("cancelled");
    }
  };
}

function armCountingScope(scope) {
  if (!scope) return;
  if (scope.dataset.countingArmed === "1") return;
  scope.dataset.countingArmed = "1";
  dlog("armScope", dumpEl(scope));

  let mo = null;
  let gateCancel = null;
  let gateArmedAt = 0;
  let cleanTO = null;

  function clearGate() {
    if (typeof gateCancel === "function") gateCancel();
    gateCancel = null;
    gateArmedAt = 0;
  }

  function clearCleanupInterval() {
    if (cleanTO != null) {
      clearInterval(cleanTO);
      cleanTO = null;
    }
  }

  function disposeArming() {
    clearGate();
    clearCleanupInterval();
    if (mo) {
      try { mo.disconnect(); } catch {}
      mo = null;
    }
    document.removeEventListener("ani:inview-enter", onEnter, true);
    dlog("scope cleanup observers", dumpEl(scope));
  }

  function tryStart(source = "unknown") {
    dlog("tryStart", { source, scope: dumpEl(scope) });
    if (scope.dataset.countingDone === "1") {
      disposeArming();
      return;
    }
    if (gateCancel) return;
    const cancel = waitForStableVisible(scope, {
      source,
      onPass: () => {
        gateCancel = null;
        gateArmedAt = 0;
        if (scope.dataset.countingDone === "1") {
          disposeArming();
          return;
        }
        startCountingStatementsInScope(scope);
        scope.dataset.countingDone = "1";
        disposeArming();
      },
      onAbort: (reason) => {
        gateCancel = null;
        gateArmedAt = 0;
        if (scope.dataset.countingDone === "1") return;
        if (reason === "lost-visibility" || reason === "not-ready-initial") {
          requestAnimationFrame(() => {
            if (scope.dataset.countingDone === "1") return;
            if (gateCancel) return;
            if (!isScopeReadyToStart(scope)) return;
            tryStart("gate-abort-retry");
          });
        }
      }
    });
    if (typeof cancel === "function") {
      gateCancel = cancel;
      gateArmedAt = performance.now();
    }
  }

  function onEnter(ev) {
    if (!ev?.target) return;
    if (ev.target === scope || ev.target.contains(scope)) {
      dlog("inview-enter caught for scope", dumpEl(scope));
      tryStart("ani:inview-enter");
    }
  }

  mo = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "attributes" && m.attributeName === "class") {
        dlog("scope class mutation", dumpEl(scope));
        tryStart("scope-class-mutation");
        break;
      }
    }
  });
  mo.observe(scope, { attributes: true, attributeFilter: ["class"] });
  document.addEventListener("ani:inview-enter", onEnter, true);

  tryStart("arm-initial");

  cleanTO = setInterval(() => {
    if (scope.dataset.countingDone === "1") {
      disposeArming();
      return;
    }
    if (gateCancel && gateArmedAt > 0) {
      const gateAge = performance.now() - gateArmedAt;
      if (gateAge > 4000) clearGate();
    }
    if (!gateCancel && scope.dataset.countingDone !== "1" && isScopeReadyToStart(scope)) {
      tryStart("cleanup-visible-retry");
    }
  }, 2000);
  window.addEventListener("beforeunload", () => {
    clearGate();
    clearCleanupInterval();
  }, { once: true });
}

export function startCountingStatements(root = document) {
  const scopes = [...root.querySelectorAll(".ani-counting-scope")];
  dlog("startCountingStatements (arm all scopes under root)", { count: scopes.length, root: dumpEl(root) });
  scopes.forEach(armCountingScope);
}

export function runCountingNow(scopeEl) {
  if (!scopeEl) return;
  dlog("runCountingNow (force)", dumpEl(scopeEl));
  startCountingStatementsInScope(scopeEl);
  scopeEl.dataset.countingDone = "1";
}

export function prepareCountingStatementsAni() {
  if (!window.__visStatementHoverTrackInit__) {
    window.__visStatementHoverTrackInit__ = true;
    document.addEventListener("pointermove", (ev) => {
      const t = ev.target;
      if (!(t instanceof Element)) return;
      const card = t.closest(".vis-statement-content");
      if (!card || card.closest(".vis-tools-logo-card")) return;
      const r = card.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const x = ((ev.clientX - r.left) / r.width) * 100;
      const y = ((ev.clientY - r.top) / r.height) * 100;
      const cx = Math.max(0, Math.min(100, x));
      const cy = Math.max(0, Math.min(100, y));
      card.style.setProperty("--hover-x", `${cx}%`);
      card.style.setProperty("--hover-y", `${cy}%`);
    }, { passive: true });
  }

  document.addEventListener("ani:inview-enter", (ev) => {
    const root = ev.target;
    const scopes = root.matches?.(".ani-counting-scope")
      ? [root]
      : [...root.querySelectorAll(".ani-counting-scope")];
    dlog("ani:inview-enter (wiring) -> arming scopes", { triggeredBy: dumpEl(root), scopes: scopes.length });
    scopes.forEach(armCountingScope);
  }, { capture: true });

  document.addEventListener("pointerenter", (ev) => {
    const t = ev.target;
    if (!t?.closest) return;
    const scope = t.closest("[data-ani-count=\"hover\"], .ani-counting-scope[data-ani-count=\"hover\"]");
    if (scope) {
      dlog("pointerenter -> arm scope (hover)", dumpEl(scope));
      armCountingScope(scope);
    }
  }, { capture: true });

  document.addEventListener("focusin", (ev) => {
    const t = ev.target;
    if (!t?.closest) return;
    const scope = t.closest("[data-ani-count=\"focus\"], .ani-counting-scope[data-ani-count=\"focus\"]");
    if (scope) {
      dlog("focusin -> arm scope (focus)", dumpEl(scope));
      armCountingScope(scope);
    }
  }, { capture: true });

  const boot = () => {
    const all = document.querySelectorAll("[data-ani-count=\"auto\"], .ani-counting-scope[data-ani-count=\"auto\"], .ani-counting-scope");
    dlog("prepareCounting boot -> arming all found scopes", { count: all.length });
    all.forEach(armCountingScope);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();

  const rootMO = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type !== "childList" || !m.addedNodes?.length) continue;
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(".ani-counting-scope")) armCountingScope(node);
        const scopes = node.querySelectorAll?.(".ani-counting-scope");
        if (scopes?.length) {
          dlog("dom insertion -> arming scopes", { added: dumpEl(node), scopes: scopes.length });
          scopes.forEach(armCountingScope);
        }
      }
    }
  });
  rootMO.observe(document.documentElement, { subtree: true, childList: true });

  dlog("prepareCountingStatementsAni ready");

  return () => {
    try { rootMO.disconnect(); } catch {}
  };
}
